//@flow
import React, {useState} from 'react';
import {
  makeStyles,
  Grid,
  Typography,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import {Pagination} from '@material-ui/lab';
import {ViewModule, ViewList} from '@material-ui/icons';
import {Link} from 'react-router-dom';

import {colors} from '../../../../../assets/styles/colors';
import {useLocalStorage} from '../../../../../utils/dom';
import {classNames} from '../../../../../utils/react/className';
import {
  useBlogPostActions,
  useBlogPostFilter,
  useBlogPostFilterActions,
  useBlogPostList,
  useBlogPostPagination,
  useBlogPostPaginationActions,
} from '../../../utils/blog-post';
import ConfirmModal from '../../../../../elements/Modals/ConfirmModal';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {useIsFeatureInTier} from '../../../../../utils/component';
import {useTierRestrictionActions} from '../../../../TierRestriction/utils';
import Toast from '../../../../../elements/Toast';
import BlogCard from './BlogCard';
import BlogCardList from './BlogCardList';
import SearchInput from '../../../../../elements/SearchInput';
import {useCallbackDebounce} from '../../../../../utils/react';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '.75rem',
    flexGrow: 1,
    overflowY: 'auto',
    minHeight: 0,
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
    },
    '&::-webkit-scrollbar-track': {
      background: colors.grey200,
      borderRadius: '5px',
    },
    '&::-webkit-scrollbar-thumb': {
      background: colors.grey400,
      borderRadius: '5px',
    },
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
    gap: '0.5rem',
  },
  layoutButtons: {
    display: 'flex',
    gap: '.5rem',
    alignItems: 'center',
  },
  layoutButton: {
    color: colors.grey500,
    '&.active': {
      color: colors.primary,
      backgroundColor: colors.lightPurple,
    },
  },
  emptyTableContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '3rem 1rem',
    width: '100%',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    padding: '1rem',
  },
}));

export default function BlogList() {
  const classes = useStyles();
  const blogPosts = useBlogPostList();
  const pagination = useBlogPostPagination();
  const {setPage} = useBlogPostPaginationActions();

  const hasBlogFeature = useIsFeatureInTier('STORE_BLOG');
  const {showTierRestrictionModal} = useTierRestrictionActions();

  const {setBlogPostSearch} = useBlogPostFilterActions();
  const filters = useBlogPostFilter();

  const {
    deleteBlogPost,
    toggleBlogPostPublish,
    togglePostFeatured,
    error,
  } = useBlogPostActions();

  const [idToDelete, setIdToDelete] = useState<string | null>(null);
  const [layout, setLayout] = useLocalStorage('blog_list_layout', 'grid');

  const handleInputChange = useCallbackDebounce(
    keyword => {
      setBlogPostSearch(keyword);
    },
    [setBlogPostSearch],
    500
  );

  const keyword = filters?.title;

  if (blogPosts.length === 0 && keyword?.length === 0) {
    return (
      <>
        <div className={classes.emptyTableContainer}>
          <Typography variant="body1">No hay artículos de blog</Typography>

          <CustomButton
            variant="outlined"
            component={Link}
            to="/my-store/blog/new"
            onClick={e => {
              if (!hasBlogFeature) {
                e.preventDefault();
                showTierRestrictionModal();
              }
            }}
          >
            Crear nuevo artículo
          </CustomButton>
        </div>
        <Toast open={!!error} closeDelay={5000} severity="error">
          {error}
        </Toast>
      </>
    );
  }

  return (
    <>
      <div className={classes.container}>
        <div className={classes.controls}>
          <SearchInput
            placeholder="Buscar por título"
            onChange={handleInputChange}
          />
          <div className={classes.layoutButtons}>
            <Tooltip title="Vista de cuadrícula">
              <IconButton
                size="small"
                className={classNames(classes.layoutButton, {
                  active: layout === 'grid',
                })}
                onClick={() => setLayout('grid')}
              >
                <ViewModule />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vista de lista">
              <IconButton
                size="small"
                className={classNames(classes.layoutButton, {
                  active: layout === 'list',
                })}
                onClick={() => setLayout('list')}
              >
                <ViewList />
              </IconButton>
            </Tooltip>
          </div>
        </div>

        <Grid container spacing={3}>
          {blogPosts.map(post => {
            const CardComponent = layout === 'list' ? BlogCardList : BlogCard;
            return (
              <Grid
                item
                xs={12}
                sm={layout === 'grid' ? 6 : 12}
                md={layout === 'grid' ? 4 : 12}
                lg={layout === 'grid' ? 3 : 12}
                key={post.id}
              >
                <CardComponent
                  post={post}
                  hasBlogFeature={hasBlogFeature}
                  onDelete={setIdToDelete}
                  onTogglePublish={toggleBlogPostPublish}
                  onToggleFeatured={togglePostFeatured}
                  showTierRestrictionModal={showTierRestrictionModal}
                />
              </Grid>
            );
          })}
        </Grid>

        {blogPosts.length === 0 && (
          <div className={classes.emptyTableContainer}>
            <Typography variant="body1">
              No se encontraron resultados
            </Typography>
          </div>
        )}
      </div>

      <footer className={classes.footer}>
        <Pagination
          count={
            pagination &&
            (pagination.size >= pagination.total
              ? 1
              : Math.ceil(pagination.total / pagination.size))
          }
          page={pagination?.page + 1 || 1}
          color="primary"
          onChange={(e, page) => {
            setPage(page - 1);
          }}
        />
      </footer>

      <ConfirmModal
        open={!!idToDelete}
        message="El blog se eliminará permanentemente."
        onCancel={() => setIdToDelete(null)}
        onClose={() => setIdToDelete(null)}
        onConfirm={() => {
          if (!idToDelete) return;
          deleteBlogPost(idToDelete).then(() => {
            setIdToDelete(null);
          });
        }}
      />

      <Toast open={!!error} closeDelay={5000} severity="error">
        {error}
      </Toast>
    </>
  );
}
